import type { DeepContent } from "@/components/deep-content";

/**
 * Long-form guidance appended beneath each glossary definition.
 * Definition pages answer "what is it"; these sections answer
 * "how does it work in practice for a UK employer".
 */
export const glossaryDepth: Record<string, DeepContent> = {
  "constructive-dismissal": {
    title: "Constructive dismissal in practice",
    intro:
      "Most constructive dismissal claims begin long before the resignation letter arrives. They start with an unresolved grievance, a pay or duties change made without agreement, or a manager who is allowed to keep behaving badly. The test below is what a tribunal actually applies.",
    sections: [
      {
        heading: "The four-part test a tribunal applies",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "There was a breach of contract by the employer, either of an express term such as pay, hours or place of work, or of the implied term of trust and confidence.",
              "The breach was fundamental, meaning it went to the root of the contract rather than being a minor irritation.",
              "The employee resigned in response to that breach, and not for an unrelated reason such as a better job offer.",
              "The employee did not delay so long that they are treated as having accepted the breach and affirmed the contract.",
            ],
          },
          {
            kind: "p",
            text: "Only once those four points are established does the tribunal move on to whether the dismissal was fair. Employers sometimes win at that second stage, but the reputational and legal cost of getting to it is high.",
          },
        ],
      },
      {
        heading: "Common triggers we see in employer cases",
        blocks: [
          {
            kind: "table",
            head: ["Trigger", "Why it becomes a breach", "Safer alternative"],
            rows: [
              [
                "Unilateral pay or hours change",
                "Changing a contractual term without consent is an immediate express breach.",
                "Consult, seek written agreement, or use a properly run dismissal and re-engagement process with advice.",
              ],
              [
                "Demotion after a disciplinary",
                "Imposing a sanction that the contract does not allow for breaches trust and confidence.",
                "Check the contract and handbook for a demotion clause before offering it as an alternative to dismissal.",
              ],
              [
                "Ignoring a grievance",
                "Failing to investigate a serious complaint is a classic implied-term breach.",
                "Acknowledge within five working days, hear it, and give a written outcome with a right of appeal.",
              ],
              [
                "Bullying by a manager",
                "The employer is liable for the conduct of managers acting in the course of employment.",
                "Investigate promptly, separate the parties where needed, and record what you did.",
              ],
              [
                "A 'last straw' incident",
                "A minor act can revive earlier conduct and complete a cumulative breach.",
                "Treat repeat complaints from the same employee as a pattern, not isolated events.",
              ],
            ],
          },
        ],
      },
      {
        heading: "What an employer should do when resignation is threatened",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Do not accept the resignation on the spot. Ask for a short pause and offer to hear the underlying concern.",
              "Convert the complaint into a formal grievance so there is a documented process to point to.",
              "Suspend the disputed change, for example a rota or reporting-line move, while the grievance is heard.",
              "Take a written note of every conversation, including offers made and refused.",
              "Where the relationship cannot be repaired, take advice on whether a settlement agreement is proportionate.",
            ],
          },
          {
            kind: "callout",
            title: "Time limits",
            text: "A constructive unfair dismissal claim must normally be started through ACAS early conciliation within three months less one day of the effective date of termination, which is the last day of employment after any notice worked.",
          },
        ],
      },
      {
        heading: "How much a constructive dismissal claim can cost",
        blocks: [
          {
            kind: "p",
            text: "Compensation follows the ordinary unfair dismissal rules: a basic award calculated like statutory redundancy pay, plus a compensatory award for lost earnings capped at the statutory maximum or 52 weeks' pay, whichever is lower. Awards can rise by up to 25% where the employer unreasonably failed to follow the ACAS Code, and discrimination claims running alongside are uncapped and can include injury to feelings.",
          },
          {
            kind: "p",
            text: "Defence costs are rarely recoverable in the employment tribunal, so even a successful defence carries a real cost in management time and legal fees. Early, documented resolution is almost always cheaper.",
          },
        ],
      },
    ],
  },

  "unfair-dismissal": {
    title: "Getting a dismissal right: reason, reasonableness and process",
    intro:
      "Unfair dismissal claims are usually lost on process rather than substance. The reason for dismissal may be entirely genuine, but if the investigation was thin or the employee was not given a fair hearing, the dismissal will still be unfair.",
    sections: [
      {
        heading: "The five potentially fair reasons",
        blocks: [
          {
            kind: "table",
            head: ["Reason", "Typical evidence a tribunal expects"],
            rows: [
              ["Conduct", "Investigation report, witness statements, disciplinary invite letter, hearing notes, outcome and appeal."],
              ["Capability or qualifications", "Performance data, objectives set, support and training offered, review meetings, medical evidence in ill-health cases."],
              ["Redundancy", "Business case, pooling and selection criteria, scoring, consultation records, search for alternative employment."],
              ["Statutory restriction", "Evidence the employment could not lawfully continue, for example a lost right to work or a driving ban for a driver, plus consideration of alternatives."],
              ["Some other substantial reason", "Business reorganisation papers, refusal of a reasonable contract change, or an irretrievable breakdown in working relationships."],
            ],
          },
        ],
      },
      {
        heading: "The fair procedure checklist",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Investigate before you accuse. Appoint someone independent of the allegation where you can.",
              "Put the allegations in writing, attach the evidence, and give reasonable notice of the hearing.",
              "Tell the employee they may be accompanied by a colleague or trade union representative.",
              "Hold the hearing, let them respond, and adjourn to consider rather than deciding in the room.",
              "Give the outcome in writing with reasons and a right of appeal.",
              "Hear the appeal, ideally by a more senior manager who has not been involved.",
            ],
          },
          {
            kind: "callout",
            title: "Band of reasonable responses",
            text: "A tribunal does not ask what it would have done. It asks whether dismissal fell within the range of responses a reasonable employer could have taken. Consistency with how you treated comparable cases in the past is a major part of that.",
          },
        ],
      },
      {
        heading: "Who can claim, and when the two-year rule does not apply",
        blocks: [
          {
            kind: "p",
            text: "Most employees need two years' continuous service. That qualifying period does not apply where the dismissal is automatically unfair, including dismissals connected with whistleblowing, health and safety activities, trade union membership, pregnancy or family leave, asserting a statutory right, or a TUPE transfer. Discrimination claims under the Equality Act 2010 have no service requirement at all.",
          },
          {
            kind: "p",
            text: "Because automatically unfair and discrimination claims can be brought from day one, short-service dismissals still need a documented, defensible reason.",
          },
        ],
      },
      {
        heading: "Reducing exposure before you dismiss",
        blocks: [
          {
            kind: "list",
            items: [
              "Ask whether a written warning, redeployment or a performance plan would achieve the same result.",
              "Check length of service, protected characteristics, live grievances and any recent absence or complaint that could support a victimisation argument.",
              "Follow the ACAS Code of Practice on disciplinary and grievance procedures. Failing to do so can add up to 25% to any award.",
              "Where you want certainty, consider a protected conversation under section 111A of the Employment Rights Act 1996 and a settlement agreement.",
            ],
          },
        ],
      },
    ],
  },

  "tupe-transfer": {
    title: "Running a TUPE transfer without a claim",
    intro:
      "TUPE applies far more often than employers expect. It covers business sales and also service provision changes, so it catches outsourcing, insourcing and contractor changes in cleaning, catering, care, facilities and IT support.",
    sections: [
      {
        heading: "When TUPE applies",
        blocks: [
          {
            kind: "list",
            items: [
              "A business transfer: an economic entity retaining its identity moves to a new owner, for example an asset sale of a trading division.",
              "A service provision change: activities are outsourced to a contractor, brought back in-house, or moved from one contractor to another.",
              "There must be an organised grouping of employees whose principal purpose is carrying out those activities for the client.",
              "TUPE does not apply to a share sale, because the employer entity does not change, and it does not usually apply to a one-off supply of goods or a short-term task.",
            ],
          },
        ],
      },
      {
        heading: "Obligations on each side",
        blocks: [
          {
            kind: "table",
            head: ["Stage", "Outgoing employer (transferor)", "Incoming employer (transferee)"],
            rows: [
              [
                "Before transfer",
                "Provide employee liability information at least 28 days before the transfer.",
                "Confirm any measures it plans to take so the transferor can inform and consult on them.",
              ],
              [
                "Consultation",
                "Inform recognised representatives, or elect them where none exist, and consult on measures.",
                "Cooperate with the process and supply accurate measures information.",
              ],
              [
                "On transfer",
                "Employment ends only in name. Continuity, terms and liabilities pass across.",
                "Inherits contracts, continuity of service, and most liabilities including outstanding claims.",
              ],
              [
                "After transfer",
                "Retains liability for anything it failed to disclose or consult on.",
                "Can only change terms for an economic, technical or organisational reason entailing changes in the workforce.",
              ],
            ],
          },
        ],
      },
      {
        heading: "The measures and consultation trap",
        blocks: [
          {
            kind: "p",
            text: "Failure to inform and consult carries a protective award of up to 13 weeks' actual pay per affected employee, and both employers can be jointly liable. That is often the single largest exposure in a small transfer, because it applies per head regardless of whether anyone lost their job.",
          },
          {
            kind: "p",
            text: "Where there is no recognised union and no existing employee forum, you must arrange an election of representatives. Employers with fewer than ten employees may consult staff directly, but the substance of the duty stays the same.",
          },
        ],
      },
      {
        heading: "Harmonising terms after a transfer",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Map the two sets of terms and identify genuine differences in pay, hours, notice, holiday, sick pay and pensions.",
              "Accept that harmonisation purely because of the transfer is void, even if the employee agrees to it.",
              "Look for a separate, genuine ETO reason such as a restructure with real changes in numbers or functions.",
              "Where change is unavoidable, consult properly, document the business reason, and consider a package that leaves employees no worse off overall.",
              "Take advice before dismissing and re-engaging. Transfer-connected dismissals are automatically unfair without an ETO reason.",
            ],
          },
          {
            kind: "callout",
            title: "Practical tip",
            text: "Start the employee liability information exercise as soon as heads of terms are agreed. Late or inaccurate data is the most common cause of a transfer date slipping and of disputes between the two employers afterwards.",
          },
        ],
      },
    ],
  },

  pilon: {
    title: "Using payment in lieu of notice correctly",
    intro:
      "PILON looks simple: pay the notice and end the contract today. The tax treatment and the effect on restrictive covenants are where employers get caught out.",
    sections: [
      {
        heading: "Contractual PILON versus a payment without a clause",
        blocks: [
          {
            kind: "table",
            head: ["", "PILON clause in the contract", "No PILON clause"],
            rows: [
              ["Nature of payment", "Contractual payment for the notice period.", "Damages for breach of contract by the employer."],
              ["Restrictive covenants", "Remain enforceable, because you are performing the contract.", "At risk. A repudiatory breach can release the employee from post-termination restrictions."],
              ["Garden leave", "Available as an alternative if the contract allows it.", "Placing someone on garden leave without a clause is itself risky."],
              ["Tax", "Taxable as earnings in full.", "Still taxable as post-employment notice pay under the rules below."],
            ],
          },
        ],
      },
      {
        heading: "Tax: post-employment notice pay",
        blocks: [
          {
            kind: "p",
            text: "Since April 2018 every employer must calculate post-employment notice pay (PENP) on termination. PENP is the portion of any termination payment that represents unworked basic notice. It is taxed as earnings and attracts both employee and employer National Insurance, whether or not the contract contains a PILON clause. Only the balance above PENP can benefit from the £30,000 termination payment exemption.",
          },
          {
            kind: "p",
            text: "The statutory formula uses basic pay in the last pay period before notice, the number of days in that period, and the number of unworked notice days. Bonuses, commission and benefits in kind are excluded from the basic pay figure, which often surprises employers of commission-heavy roles.",
          },
        ],
      },
      {
        heading: "PILON, garden leave or worked notice: choosing",
        blocks: [
          {
            kind: "list",
            items: [
              "Worked notice suits a clean handover where there is no competitive or conduct risk.",
              "Garden leave keeps the employee bound by the full contract, including duties of loyalty and confidentiality, while keeping them away from clients. Use it where covenants matter most.",
              "PILON ends the relationship immediately and stops holiday accruing, which is useful where trust has broken down or the role has gone.",
              "Whichever route you take, deal with accrued untaken holiday, company property, benefits end dates, and any pension contributions in the same letter.",
            ],
          },
          {
            kind: "callout",
            title: "Check before you pay",
            text: "Statutory notice under section 86 of the Employment Rights Act 1996 is one week per complete year of service up to twelve weeks. If the contract gives less, the statutory minimum still applies and the PILON must reflect it.",
          },
        ],
      },
    ],
  },

  "settlement-agreement": {
    title: "How employers use settlement agreements",
    intro:
      "A settlement agreement buys certainty. In exchange for a payment, the employee waives specified claims, so the risk of a tribunal claim over that dispute ends on signature. Getting the conditions right is what makes it binding.",
    sections: [
      {
        heading: "Statutory conditions for a valid agreement",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "The agreement is in writing.",
              "It relates to a particular complaint or particular proceedings, so a blanket waiver of unknown claims will not work on its own.",
              "The employee has received advice from a relevant independent adviser, usually a solicitor, on the terms and effect of the agreement.",
              "The adviser is identified in the agreement and covered by professional indemnity insurance.",
              "The agreement states that the statutory conditions regulating settlement agreements are satisfied.",
            ],
          },
          {
            kind: "p",
            text: "Some rights cannot be settled, including accrued pension rights in most cases, personal injury claims the employee does not yet know about, and the right to enforce the agreement itself.",
          },
        ],
      },
      {
        heading: "What a typical package contains",
        blocks: [
          {
            kind: "table",
            head: ["Element", "Typical treatment"],
            rows: [
              ["Notice pay", "Paid as PILON and taxed as earnings, subject to post-employment notice pay rules."],
              ["Ex gratia compensation", "First £30,000 can be tax free where it is genuinely for loss of employment rather than for services."],
              ["Accrued holiday", "Paid through payroll with tax and National Insurance."],
              ["Legal fee contribution", "£350 to £750 plus VAT is common, paid directly to the adviser."],
              ["Reference", "An agreed wording annexed to the agreement, usually factual dates and job title."],
              ["Confidentiality and non-derogatory clauses", "Must not prevent protected disclosures, reporting a crime, or cooperating with a regulator."],
            ],
          },
        ],
      },
      {
        heading: "Protected conversations and without prejudice",
        blocks: [
          {
            kind: "p",
            text: "Section 111A of the Employment Rights Act 1996 lets an employer open a settlement discussion that cannot be referred to in an ordinary unfair dismissal claim, even where there is no existing dispute. The protection is lost if there is improper behaviour, such as undue pressure, or if the claim is for discrimination, whistleblowing or automatic unfair dismissal.",
          },
          {
            kind: "p",
            text: "Give the employee a reasonable period to consider the offer. The ACAS Code on settlement agreements suggests a minimum of ten calendar days, and pressing for a same-day signature is the behaviour most likely to lose the protection.",
          },
          {
            kind: "callout",
            title: "Order of play",
            text: "Run any live disciplinary, capability or redundancy process properly in parallel. If settlement talks fail, you need a fair process you can still complete, not a half-finished one that has been overtaken by the negotiation.",
          },
        ],
      },
    ],
  },

  "gross-misconduct": {
    title: "Handling a gross misconduct allegation",
    intro:
      "The label gross misconduct does not remove the need for a fair process. Summary dismissal is only safe where the conduct genuinely destroys trust and where the employer investigated, heard the employee, and considered alternatives.",
    sections: [
      {
        heading: "Examples that usually qualify, and ones that often do not",
        blocks: [
          {
            kind: "table",
            head: ["Usually gross misconduct", "Usually ordinary misconduct"],
            rows: [
              ["Theft, fraud or deliberate falsification of records", "One-off lateness or a short unauthorised absence"],
              ["Physical violence or serious threats at work", "Rudeness that has not been previously addressed"],
              ["Serious breach of health and safety rules", "A procedural mistake without recklessness"],
              ["Deliberate damage to property or data", "Poor performance, which belongs in a capability process"],
              ["Serious harassment, bullying or discrimination", "Minor misuse of email or the internet"],
              ["Working under the influence in a safety-critical role", "A first breach of a rule that was never communicated"],
            ],
          },
          {
            kind: "p",
            text: "Whether conduct is gross depends on the role, the sector and what your handbook says. List examples in the disciplinary policy and make sure staff have seen it, because an unwritten rule is much harder to enforce.",
          },
        ],
      },
      {
        heading: "The process, step by step",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Consider suspension only where it is genuinely necessary, keep it short, on full pay, and confirm it is neutral and not a sanction.",
              "Investigate: gather CCTV, system logs, statements and the employee's own account before any decision is formed.",
              "Write to the employee setting out the allegations, enclosing the evidence, and confirming that dismissal is a possible outcome.",
              "Hold the hearing with a note taker, allow a companion, and let the employee respond to each allegation.",
              "Adjourn, decide, and record why the sanction chosen was reasonable and consistent with previous cases.",
              "Confirm in writing, with the date of termination and the right of appeal.",
            ],
          },
        ],
      },
      {
        heading: "Pay and notice on summary dismissal",
        blocks: [
          {
            kind: "list",
            items: [
              "No notice or pay in lieu of notice is due where gross misconduct is established.",
              "Accrued untaken statutory holiday must still be paid.",
              "You cannot withhold wages already earned. Deductions need a contractual right or written consent.",
              "Where the misconduct also causes loss, recovery is a separate civil matter and should not be handled through the payroll without advice.",
            ],
          },
          {
            kind: "callout",
            title: "Reasonable belief, not proof",
            text: "The employer does not need proof beyond reasonable doubt. The standard is a genuine belief in the misconduct, held on reasonable grounds, after as much investigation as was reasonable in the circumstances.",
          },
        ],
      },
    ],
  },

  redundancy: {
    title: "From the legal definition to a defensible redundancy",
    intro:
      "Redundancy is a legal test about the work, not about the person. Two things sink most redundancy defences: a selection process that looks designed around an individual, and consultation that started after the decision was made.",
    sections: [
      {
        heading: "Does the situation meet the statutory definition?",
        blocks: [
          {
            kind: "list",
            items: [
              "Closure of the business as a whole.",
              "Closure of the particular workplace where the employee works.",
              "A reduced requirement for employees to do work of a particular kind, at that workplace or generally.",
            ],
          },
          {
            kind: "p",
            text: "If the same work is continuing at the same volume and you simply want a different person doing it, that is not redundancy. Attempting to use redundancy to remove a performance or conduct problem is one of the fastest routes to an unfair dismissal finding.",
          },
        ],
      },
      {
        heading: "Pooling, selection and scoring",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Define the pool by looking at who does interchangeable work, not by who you would prefer to keep.",
              "Choose criteria that are objective and capable of evidence: skills and qualifications, performance records, disciplinary record within a defined period, and attendance excluding absence linked to disability, pregnancy or family leave.",
              "Have two managers score independently where possible and keep the underlying evidence.",
              "Share individual scores with the employee at consultation and allow challenge.",
              "Avoid criteria such as flexibility or attitude unless you can define and evidence them.",
            ],
          },
        ],
      },
      {
        heading: "Consultation timetables",
        blocks: [
          {
            kind: "table",
            head: ["Proposed redundancies at one establishment", "Minimum consultation", "Who to consult"],
            rows: [
              ["Fewer than 20 in 90 days", "No fixed minimum, but meaningful individual consultation is required", "Each affected employee individually"],
              ["20 to 99 in 90 days", "At least 30 days before the first dismissal takes effect", "Recognised union or elected employee representatives, plus individuals"],
              ["100 or more in 90 days", "At least 45 days before the first dismissal takes effect", "Recognised union or elected representatives, plus individuals, with an HR1 form to the Insolvency Service"],
            ],
          },
          {
            kind: "callout",
            title: "Collective consultation penalties",
            text: "Failing to consult collectively where required can lead to a protective award of up to 90 days' actual pay per affected employee, and failing to file form HR1 is a criminal offence.",
          },
        ],
      },
      {
        heading: "Payments and final steps",
        blocks: [
          {
            kind: "list",
            items: [
              "Statutory redundancy pay is based on age, complete years of service capped at 20, and a capped weekly pay figure.",
              "Employees with two years' service are entitled to reasonable paid time off to look for work.",
              "Suitable alternative employment must be offered where it exists anywhere in the group, with a four-week statutory trial period.",
              "Employees on maternity, adoption or shared parental leave have enhanced protection and must be offered a suitable alternative vacancy ahead of others.",
              "Give notice in writing, confirm the redundancy payment breakdown, and offer a right of appeal.",
            ],
          },
        ],
      },
    ],
  },
};

export function getGlossaryDepth(slug: string): DeepContent | undefined {
  return glossaryDepth[slug];
}
